import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { format } from "date-fns";
import { CalendarDays, CheckSquare, LogOut, Sun, Plus, LayoutGrid, RefreshCw, UserRound, CalendarPlus, ChevronLeft } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { InstallPWA } from "../../components/InstallPWA";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../components/ui/select";
import { TODO_COLORS } from "../../lib/todoColors";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";

const LINKS = [
  { to: "/planner", label: "Vandaag", short: "Vandaag", icon: Sun, end: true },
  { to: "/planner/agenda", label: "Agenda", short: "Agenda", icon: CalendarDays },
  { to: "/planner/todos", label: "To-Do's", short: "To-Do", icon: CheckSquare },
];

function QuickAddTodo() {
  const todayStr = format(new Date(), "yyyy-MM-dd");
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState("choose"); // choose | todo | private | treatment
  const [busy, setBusy] = useState(false);
  // to-do
  const [title, setTitle] = useState("");
  const [date, setDate] = useState(todayStr);
  const [time, setTime] = useState("");
  const [schedule, setSchedule] = useState(false);
  const [duration, setDuration] = useState(30);
  const [load, setLoad] = useState(2);
  // privé
  const [pTitle, setPTitle] = useState("");
  const [pDate, setPDate] = useState(todayStr);
  const [pStart, setPStart] = useState("09:00");
  const [pEnd, setPEnd] = useState("10:00");
  const [pColor, setPColor] = useState(TODO_COLORS[4].value);
  const [pLoad, setPLoad] = useState(0);
  // behandeling
  const [treatments, setTreatments] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [tTreatment, setTTreatment] = useState("");
  const [tCustomer, setTCustomer] = useState("");
  const [custSearch, setCustSearch] = useState("");
  const [tDate, setTDate] = useState(todayStr);
  const [tStart, setTStart] = useState("09:00");

  useEffect(() => {
    if (!open) return;
    api.get("/admin/treatments").then((r) => setTreatments((r.data || []).filter((t) => t.schedulable && t.active))).catch(() => {});
    api.get("/admin/customers").then((r) => setCustomers(r.data || [])).catch(() => {});
  }, [open]);

  const reset = () => {
    setKind("choose"); setTitle(""); setDate(todayStr); setTime(""); setSchedule(false); setDuration(30); setLoad(2);
    setPTitle(""); setPDate(todayStr); setPStart("09:00"); setPEnd("10:00"); setPColor(TODO_COLORS[4].value); setPLoad(0);
    setTTreatment(""); setTCustomer(""); setTDate(todayStr); setTStart("09:00"); setCustSearch("");
  };
  const close = () => { setOpen(false); reset(); };
  const toMin = (t) => { const [H, M] = (t || "0:0").split(":").map(Number); return H * 60 + M; };

  const saveTodo = async () => {
    if (!title.trim()) { toast.error("Geef de to-do een titel."); return; }
    if (schedule && !time) { toast.error("Kies een tijd om in te plannen."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/todos", {
        title: title.trim(), notes: "", due_date: date, due_time: time,
        duration_min: schedule ? (Number(duration) || 30) : 0, load: Number(load) || 0,
        color: TODO_COLORS[0].value, repeat: "none", subtasks: [],
      });
      if (schedule && time && data?.id) await api.post(`/admin/todos/${data.id}/schedule`, { start: `${date}T${time}:00` });
      toast.success(schedule ? "To-do toegevoegd en op de agenda gezet." : "To-do toegevoegd.");
      window.dispatchEvent(new Event("planner:refresh")); close();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const savePrivate = async () => {
    if (!pTitle.trim()) { toast.error("Geef de privé afspraak een titel."); return; }
    if (toMin(pEnd) <= toMin(pStart)) { toast.error("Eindtijd moet na de starttijd liggen."); return; }
    setBusy(true);
    try {
      await api.post("/admin/blocks", { date: pDate, start: pStart, end: pEnd, kind: "private", title: pTitle.trim(), color: pColor, load: Number(pLoad) || 0, note: "" });
      toast.success("Privé afspraak toegevoegd.");
      window.dispatchEvent(new Event("planner:refresh")); close();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const saveTreatment = async () => {
    if (!tTreatment) { toast.error("Kies een behandeling."); return; }
    if (!tCustomer) { toast.error("Kies een klant."); return; }
    setBusy(true);
    try {
      await api.post("/admin/appointments", { customer_id: tCustomer, treatment_id: tTreatment, start: `${tDate}T${tStart}:00`, notes: "", send_confirmation: true });
      const c = customers.find((x) => x.id === tCustomer);
      toast.success(c && (c.email || c.phone) ? "Afspraak ingepland. Bevestiging verstuurd." : "Afspraak ingepland.");
      window.dispatchEvent(new Event("planner:refresh")); close();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const heading = kind === "todo" ? "Snel een to-do" : kind === "private" ? "Privé afspraak" : kind === "treatment" ? "Behandeling inplannen" : "Wat wil je toevoegen?";

  return (
    <>
      <button data-testid="planner-quick-add" onClick={() => { reset(); setOpen(true); }} aria-label="Nieuw toevoegen"
        className="fixed z-40 right-5 bottom-24 md:bottom-8 w-14 h-14 rounded-full bg-brand hover:bg-brand-hover text-white shadow-[0_10px_30px_rgb(217,165,165,0.5)] flex items-center justify-center transition-transform hover:scale-105 active:scale-95">
        <Plus className="w-6 h-6" />
      </button>
      <Dialog open={open} onOpenChange={(o) => { if (!o) close(); else setOpen(true); }}>
        <DialogContent className="rounded-2xl max-w-sm" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              {kind !== "choose" && (
                <button data-testid="quick-back" onClick={() => setKind("choose")} className="text-ink-muted hover:text-brand"><ChevronLeft className="w-5 h-5" /></button>
              )}
              {heading}
            </DialogTitle>
          </DialogHeader>

          {kind === "choose" && (
            <div className="grid gap-2 pt-1">
              <button data-testid="quick-choose-treatment" onClick={() => setKind("treatment")} className="flex items-center gap-3 rounded-xl border border-[#E8E2D9] p-3.5 text-left hover:border-brand hover:bg-brand-light/30 transition-colors">
                <CalendarPlus className="w-5 h-5 text-brand shrink-0" />
                <div><div className="font-medium text-sm">Behandeling</div><div className="text-xs text-ink-muted">Klant + behandeling op een tijd</div></div>
              </button>
              <button data-testid="quick-choose-private" onClick={() => setKind("private")} className="flex items-center gap-3 rounded-xl border border-[#E8E2D9] p-3.5 text-left hover:border-brand hover:bg-brand-light/30 transition-colors">
                <UserRound className="w-5 h-5 text-brand shrink-0" />
                <div><div className="font-medium text-sm">Privé afspraak</div><div className="text-xs text-ink-muted">Eigen tijd blokken (van–tot)</div></div>
              </button>
              <button data-testid="quick-choose-todo" onClick={() => setKind("todo")} className="flex items-center gap-3 rounded-xl border border-[#E8E2D9] p-3.5 text-left hover:border-brand hover:bg-brand-light/30 transition-colors">
                <CheckSquare className="w-5 h-5 text-brand shrink-0" />
                <div><div className="font-medium text-sm">To-do</div><div className="text-xs text-ink-muted">Taak, met of zonder vaste tijd</div></div>
              </button>
            </div>
          )}

          {kind === "todo" && (
            <div className="space-y-4">
              <div>
                <Label className="text-sm text-ink-muted mb-1.5 block">Wat moet er gebeuren? *</Label>
                <Input data-testid="quick-todo-title" autoFocus value={title} onChange={(e) => setTitle(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") saveTodo(); }} placeholder="Bijv. voorraad bestellen" className="rounded-xl border-[#E8E2D9]" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-sm text-ink-muted mb-1.5 block">Datum</Label>
                  <Input data-testid="quick-todo-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="rounded-xl border-[#E8E2D9]" /></div>
                <div><Label className="text-sm text-ink-muted mb-1.5 block">Tijd{schedule ? " *" : ""}</Label>
                  <Input data-testid="quick-todo-time" type="time" step="900" value={time} onChange={(e) => setTime(e.target.value)} className="rounded-xl border-[#E8E2D9]" /></div>
              </div>
              <div>
                <Label className="text-sm text-ink-muted mb-1.5 block">Belasting (telt mee in de dagbelasting)</Label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((v) => (
                    <button key={v} type="button" data-testid={`quick-todo-load-${v}`} onClick={() => setLoad(v)}
                      className={`w-9 h-9 rounded-lg border text-sm transition-colors ${Number(load) === v ? "bg-brand text-white border-brand" : "bg-white text-ink-muted border-[#E8E2D9]"}`}>{v}</button>
                  ))}
                </div>
                <p className="text-xs text-ink-muted mt-1">1 = licht · 3 = normaal · 5 = zwaar.</p>
              </div>
              <div className="rounded-xl border border-[#E8E2D9] p-3 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="text-sm">Meteen op de agenda zetten</div>
                  <Switch data-testid="quick-todo-schedule" checked={schedule} onCheckedChange={setSchedule} />
                </div>
                {schedule && (
                  <div className="flex items-center gap-2">
                    <Label className="text-sm text-ink-muted">Duur</Label>
                    <Input data-testid="quick-todo-duration" type="number" min="15" step="15" value={duration} onChange={(e) => setDuration(e.target.value)} className="w-24 rounded-lg border-[#E8E2D9]" />
                    <span className="text-sm text-ink-muted">min</span>
                  </div>
                )}
                {schedule && <p className="text-xs text-ink-muted">Kies hierboven een tijd; de taak verschijnt als blok in de agenda.</p>}
              </div>
            </div>
          )}

          {kind === "private" && (
            <div className="space-y-4">
              <div>
                <Label className="text-sm text-ink-muted mb-1.5 block">Titel *</Label>
                <Input data-testid="quick-private-title" autoFocus value={pTitle} onChange={(e) => setPTitle(e.target.value)} placeholder="Bijv. tandarts, lunch" className="rounded-xl border-[#E8E2D9]" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label className="text-sm text-ink-muted mb-1.5 block">Datum</Label>
                  <Input data-testid="quick-private-date" type="date" value={pDate} onChange={(e) => setPDate(e.target.value)} className="rounded-xl border-[#E8E2D9]" /></div>
                <div><Label className="text-sm text-ink-muted mb-1.5 block">Van</Label>
                  <Input data-testid="quick-private-start" type="time" step="900" value={pStart} onChange={(e) => setPStart(e.target.value)} className="rounded-xl border-[#E8E2D9]" /></div>
                <div><Label className="text-sm text-ink-muted mb-1.5 block">Tot</Label>
                  <Input data-testid="quick-private-end" type="time" step="900" value={pEnd} onChange={(e) => setPEnd(e.target.value)} className="rounded-xl border-[#E8E2D9]" /></div>
              </div>
              <div>
                <Label className="text-sm text-ink-muted mb-1.5 block">Kleur</Label>
                <div className="flex gap-2">
                  {TODO_COLORS.map((c) => (
                    <button key={c.value} type="button" onClick={() => setPColor(c.value)} title={c.label}
                      data-testid={`quick-private-color-${c.value.replace('#', '')}`}
                      className={`w-7 h-7 rounded-full border-2 transition-transform ${pColor === c.value ? "scale-110 border-ink" : "border-white"}`}
                      style={{ backgroundColor: c.value }} />
                  ))}
                </div>
              </div>
              <div className="rounded-xl bg-[#FBEBD2] border border-dashed border-[#E0A24A] text-[#9A5B12] px-3 py-2 text-xs">Dit tijdvak wordt ook geblokkeerd voor online reserveringen.</div>
              <div>
                <Label className="text-sm text-ink-muted mb-1.5 block">Belasting (optioneel)</Label>
                <div className="flex gap-2 flex-wrap">
                  <button type="button" data-testid="quick-private-load-auto" onClick={() => setPLoad(0)}
                    className={`h-9 px-3 rounded-lg border text-sm transition-colors ${!pLoad ? "bg-brand text-white border-brand" : "bg-white text-ink-muted border-[#E8E2D9]"}`}>Auto</button>
                  {[1, 2, 3, 4, 5].map((v) => (
                    <button key={v} type="button" data-testid={`quick-private-load-${v}`} onClick={() => setPLoad(v)}
                      className={`w-9 h-9 rounded-lg border text-sm transition-colors ${Number(pLoad) === v ? "bg-brand text-white border-brand" : "bg-white text-ink-muted border-[#E8E2D9]"}`}>{v}</button>
                  ))}
                </div>
                <p className="text-xs text-ink-muted mt-1">Auto = afleiden uit de duur. Kies 1-5 om te overschrijven.</p>
              </div>
            </div>
          )}

          {kind === "treatment" && (
            <div className="space-y-4">
              <div>
                <Label className="text-sm text-ink-muted mb-1.5 block">Behandeling *</Label>
                <Select value={tTreatment} onValueChange={setTTreatment}>
                  <SelectTrigger data-testid="quick-treatment-select" className="rounded-xl border-[#E8E2D9]"><SelectValue placeholder="Kies een behandeling" /></SelectTrigger>
                  <SelectContent>
                    {treatments.map((t) => (<SelectItem key={t.id} value={t.id}>{t.name} · {t.duration_min} min</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm text-ink-muted mb-1.5 block">Klant *</Label>
                <Input data-testid="quick-customer-search" value={custSearch} onChange={(e) => setCustSearch(e.target.value)}
                  placeholder="Zoek op naam of telefoon…" className="rounded-xl border-[#E8E2D9] mb-2" />
                <Select value={tCustomer} onValueChange={setTCustomer}>
                  <SelectTrigger data-testid="quick-customer-select" className="rounded-xl border-[#E8E2D9]"><SelectValue placeholder="Kies een klant" /></SelectTrigger>
                  <SelectContent className="max-h-64">
                    {customers
                      .filter((c) => { const q = custSearch.trim().toLowerCase(); return !q || (c.name || "").toLowerCase().includes(q) || (c.phone || "").toLowerCase().includes(q) || (c.email || "").toLowerCase().includes(q); })
                      .slice(0, 50)
                      .map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}{c.phone ? ` · ${c.phone}` : ""}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-sm text-ink-muted mb-1.5 block">Datum</Label>
                  <Input data-testid="quick-treatment-date" type="date" value={tDate} onChange={(e) => setTDate(e.target.value)} className="rounded-xl border-[#E8E2D9]" /></div>
                <div><Label className="text-sm text-ink-muted mb-1.5 block">Starttijd</Label>
                  <Input data-testid="quick-treatment-start" type="time" step="900" value={tStart} onChange={(e) => setTStart(e.target.value)} className="rounded-xl border-[#E8E2D9]" /></div>
              </div>
            </div>
          )}

          {kind !== "choose" && (
            <DialogFooter>
              <Button variant="outline" onClick={close} className="rounded-full border-[#E8E2D9]">Annuleren</Button>
              <Button onClick={kind === "todo" ? saveTodo : kind === "private" ? savePrivate : saveTreatment} disabled={busy}
                data-testid="quick-save" className="rounded-full bg-brand hover:bg-brand-hover text-white">{busy ? "..." : kind === "todo" ? "Toevoegen" : "Inplannen"}</Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function OrientationRemoved() { return null; }

export default function PlannerLayout() {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const [showFull, setShowFull] = useState(false);
  const doLogout = () => { logout(); navigate("/planner/login"); };

  useEffect(() => {
    let id;
    let last = Date.now();
    const bump = () => { last = Date.now(); };
    const evs = ["mousedown", "keydown", "touchstart", "pointerdown", "input"];
    evs.forEach((e) => window.addEventListener(e, bump, { passive: true }));
    const busy = () => {
      const el = document.activeElement;
      if (el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.tagName === "SELECT" || el.isContentEditable)) return true;
      if (document.querySelector('[role="dialog"]')) return true;
      return Date.now() - last < 30000;
    };
    api.get("/admin/settings").then((r) => {
      const min = Number(r.data?.auto_refresh_min);
      if (min > 0) id = setInterval(() => { if (!busy()) window.location.reload(); }, min * 60000);
    }).catch(() => {});
    return () => { if (id) clearInterval(id); evs.forEach((e) => window.removeEventListener(e, bump)); };
  }, []);

  // Swap the active manifest + app title so this installs as its own PWA.
  useEffect(() => {
    const link = document.querySelector('link[rel="manifest"]');
    const prevManifest = link ? link.getAttribute("href") : null;
    if (link) link.setAttribute("href", "/planner-manifest.json");
    const titleMeta = document.querySelector('meta[name="apple-mobile-web-app-title"]');
    const prevTitle = titleMeta ? titleMeta.getAttribute("content") : null;
    if (titleMeta) titleMeta.setAttribute("content", "NH Planner");
    const prevDocTitle = document.title;
    document.title = "NH Planner";
    return () => {
      if (link && prevManifest) link.setAttribute("href", prevManifest);
      if (titleMeta && prevTitle) titleMeta.setAttribute("content", prevTitle);
      document.title = prevDocTitle;
    };
  }, []);

  return (
    <div className="min-h-screen bg-cream-admin flex" data-testid="planner-layout">
      {/* desktop sidebar */}
      <aside className="w-56 shrink-0 border-r border-[#E8E2D9] bg-white hidden md:flex flex-col fixed inset-y-0">
        <div className="h-20 flex items-center gap-2.5 px-6 font-display text-lg text-ink border-b border-[#E8E2D9]">
          <img src="/logo-round.png" alt="NH Planner" className="w-8 h-8" /> NH Planner
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.end} data-testid={`planner-nav-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm transition-colors duration-200 ${
                  isActive ? "bg-brand-light text-brand" : "text-ink-muted hover:bg-cream-subtle"
                }`}>
              <l.icon className="w-4 h-4" strokeWidth={1.5} /> <span>{l.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-[#E8E2D9]">
          <button onClick={() => window.location.reload()} data-testid="planner-refresh"
            className="flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm text-ink-muted hover:bg-cream-subtle w-full transition-colors mb-1">
            <RefreshCw className="w-4 h-4" strokeWidth={1.5} /> Ververs scherm
          </button>
          {showFull ? (
            <Link to="/admin" data-testid="planner-full-version"
              className="flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm text-ink-muted hover:bg-cream-subtle w-full transition-colors mb-1">
              <LayoutGrid className="w-4 h-4" strokeWidth={1.5} /> Volledige versie
            </Link>
          ) : (
            <button onClick={() => setShowFull(true)} data-testid="planner-reveal-full" title="Volledige versie tonen"
              className="flex items-center justify-center w-7 h-7 rounded-lg text-ink-muted/40 hover:text-brand hover:bg-cream-subtle transition-colors mb-1 ml-1">
              <LayoutGrid className="w-3.5 h-3.5" />
            </button>
          )}
          <InstallPWA variant="sidebar" />
          <p className="px-4 text-xs text-ink-muted mb-2 mt-1 truncate">{user?.email}</p>
          <button onClick={doLogout} data-testid="planner-logout-btn"
            className="flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm text-ink-muted hover:bg-cream-subtle w-full transition-colors">
            <LogOut className="w-4 h-4" strokeWidth={1.5} /> Uitloggen
          </button>
        </div>
      </aside>

      {/* mobile top bar */}
      <div className="md:hidden fixed top-0 inset-x-0 z-40 bg-white border-b border-[#E8E2D9] h-16 flex items-center justify-between px-4">
        <span className="font-display text-ink flex items-center gap-2">
          <img src="/logo-round.png" alt="NH Planner" className="w-7 h-7" /> NH Planner
        </span>
        <div className="flex items-center gap-4">
          <button onClick={() => window.location.reload()} data-testid="planner-refresh-mobile" aria-label="Ververs scherm" className="text-ink-muted hover:text-brand transition-colors"><RefreshCw className="w-5 h-5" /></button>
          {showFull ? (
            <Link to="/admin" data-testid="planner-full-version-mobile" aria-label="Volledige versie" className="text-ink-muted hover:text-brand transition-colors">
              <LayoutGrid className="w-5 h-5" />
            </Link>
          ) : (
            <button onClick={() => setShowFull(true)} data-testid="planner-reveal-full-mobile" aria-label="Volledige versie tonen" className="text-ink-muted/40 hover:text-brand transition-colors">
              <LayoutGrid className="w-4 h-4" />
            </button>
          )}
          <button onClick={doLogout} aria-label="Uitloggen" data-testid="planner-logout-mobile"><LogOut className="w-5 h-5 text-ink-muted" /></button>
        </div>
      </div>

      {/* mobile bottom nav */}
      <div className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-[#E8E2D9] flex justify-around py-2">
        {LINKS.map((l) => (
          <NavLink key={l.to} to={l.to} end={l.end} data-testid={`planner-bottom-nav-${l.label.toLowerCase()}`}
            className={({ isActive }) => `flex flex-col items-center gap-0.5 text-[11px] px-5 py-1 ${isActive ? "text-brand" : "text-ink-muted"}`}>
            <l.icon className="w-5 h-5" strokeWidth={1.5} /> {l.short}
          </NavLink>
        ))}
      </div>

      <QuickAddTodo />

      <main className="flex-1 min-w-0 overflow-x-hidden md:ml-56 pt-16 md:pt-0 pb-24 md:pb-0">
        <div className="max-w-6xl mx-auto p-4 sm:p-5 md:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
